import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { RiEdit2Line, RiDeleteBin6Line, RiEyeLine } from 'react-icons/ri'
import { FaUserShield, FaClipboardList } from 'react-icons/fa'
import { Button } from '../../../components/Button'
import { Loading } from '../../../components'
import type { Autorizador } from '../../../models'
import { UserAvatar } from '../../../components/UserAvatar'
import type { ProcessPermissions } from '../hooks/usePermissions'
import { useSortableTable } from '../hooks/useSortableTable'
import { SortableHeader } from './SortableHeader'

interface AutorizadoresTableProps {
  autorizadores: Autorizador[]
  permissions: ProcessPermissions
  isLoading?: boolean
  onEdit: (autorizador: Autorizador) => void
  onDelete: (autorizador: Autorizador) => void
  onView?: (autorizador: Autorizador) => void
  searchQuery?: string
}

interface ConfirmDeleteModalProps {
  isOpen: boolean
  autorizador: Autorizador | null
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
}

const ConfirmDeleteModal = ({ isOpen, autorizador, onConfirm, onCancel, isDeleting }: ConfirmDeleteModalProps) => {
  if (!isOpen || !autorizador) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full p-6"
        >
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
              <RiDeleteBin6Line className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Confirmar Exclusão
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Tem certeza que deseja excluir o autorizador{' '}
              <span className="font-semibold text-gray-900 dark:text-gray-100">&quot;{autorizador.nome}&quot;</span>?
              <br />
              <span className="text-sm text-red-600 dark:text-red-400">Esta ação não pode ser desfeita.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 justify-center">
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={isDeleting}
                className="order-2 sm:order-1"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirm}
                disabled={isDeleting}
                className="order-1 sm:order-2"
              >
                {isDeleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export const AutorizadoresTable = ({ 
  autorizadores, 
  permissions,
  isLoading = false, 
  onEdit, 
  onDelete, 
  onView,
  searchQuery = ''
}: AutorizadoresTableProps) => {
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; autorizador: Autorizador | null }>({
    isOpen: false,
    autorizador: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  // Filtrar autorizadores por busca
  const filteredAutorizadores = autorizadores.filter(autorizador => {
    if (!searchQuery) return true
    
    const searchLower = searchQuery.toLowerCase()
    return (
      autorizador.nome.toLowerCase().includes(searchLower) ||
      autorizador.usuario?.name.toLowerCase().includes(searchLower)
    )
  })

  // Hook de ordenação
  const { sortedData, sortConfig, requestSort } = useSortableTable(filteredAutorizadores)

  const handleDeleteClick = (autorizador: Autorizador) => {
    setDeleteModal({ isOpen: true, autorizador })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.autorizador) return
    
    try {
      setIsDeleting(true)
      await onDelete(deleteModal.autorizador)
      setDeleteModal({ isOpen: false, autorizador: null })
    } catch (error) {
      console.error('Erro ao excluir autorizador:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, autorizador: null })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-8 text-center space-y-4">
          <Loading message="Carregando autorizadores..." />
          <p className="text-gray-600 dark:text-gray-400">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (sortedData.length === 0 && !isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-12 text-center">
          <div className="inline-flex p-4 rounded-full bg-gray-100 dark:bg-neutral-700 mb-4">
            <FaUserShield className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery 
              ? 'Nenhum autorizador encontrado'
              : 'Nenhum autorizador cadastrado'
            }
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchQuery 
              ? `Nenhum autorizador corresponde à busca "${searchQuery}".`
              : 'Não há autorizadores cadastrados no momento.'
            }
          </p>
          {!searchQuery && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Use o botão &quot;Criar&quot; para adicionar o primeiro autorizador.
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700 overflow-hidden flex flex-col h-[calc(100vh-200px)]">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
            <thead className="bg-gray-50 dark:bg-neutral-700 sticky top-0 z-10">
              <tr>
                <SortableHeader
                  label="Autorizador"
                  sortKey="nome"
                  currentSort={sortConfig}
                  onSort={requestSort}
                />
                <SortableHeader
                  label="Usuário Vinculado"
                  sortKey="usuario.name"
                  currentSort={sortConfig}
                  onSort={requestSort}
                />
                <SortableHeader
                  label="Processos"
                  sortKey="processos"
                  currentSort={sortConfig}
                  onSort={requestSort}
                />
                <SortableHeader
                  label="Criado em"
                  sortKey="created_at"
                  currentSort={sortConfig}
                  onSort={requestSort}
                />
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
              {sortedData.map((autorizador, index) => (
                <motion.tr
                  key={autorizador.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900/30">
                        <FaUserShield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {autorizador.nome}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">ID: {autorizador.id}</div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    {autorizador.usuario ? (
                      <div className="flex items-center">
                        <UserAvatar user={autorizador.usuario} size="sm" className="mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {autorizador.usuario.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{autorizador.usuario.cargo_usuario}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400 italic">Usuário não encontrado</span>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FaClipboardList className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                        {autorizador.processos?.length || 0} {(autorizador.processos?.length || 0) === 1 ? 'processo' : 'processos'}
                      </span>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {autorizador.created_at ? new Date(autorizador.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      {onView && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onView(autorizador)}
                          className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Visualizar autorizador"
                        >
                          <RiEyeLine className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {permissions.canEditAutorizadores && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(autorizador)}
                          className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Editar autorizador"
                        >
                          <RiEdit2Line className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {permissions.canDeleteAutorizadores && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(autorizador)}
                          className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Excluir autorizador"
                        >
                          <RiDeleteBin6Line className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Mostrar se não há ações disponíveis */}
                      {!onView && !permissions.canEditAutorizadores && !permissions.canDeleteAutorizadores && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 italic">Somente leitura</span>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        autorizador={deleteModal.autorizador}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
    </>
  )
}

